'use strict';

//==> see also lib/util.js
export const FATTURA_STATUS = {
    draft: 0,
    validated: 1,
    signed: 2,
    sent: 3,
    sent_failed: 4,
    sent_success: 5,
} as const;

export const FATTURA_VERSION = {
    v1: '1',
    v1_1: '2',
    v1_2: '3',
    FSM10: '4',
    DAT20: '5',
} as const;

export const FATTURA_PRODUCT = {
    FP_MANUAL: 'NOI-X-TE',
    FP_IMPORT: 'XML',
    FP_FATTAPP_ATTIVE: 'FATTAPP_ATTIVE',
    FP_FATTAPP_PASSIVE: 'FATTAPP_PASSIVE',
    FP_CONSERV2: 'CONSERV2',
    FP_PASSIVE: 'PASSIVE',
} as const;
export type FatturaProductType = typeof FATTURA_PRODUCT[keyof typeof FATTURA_PRODUCT];

export const FATTURA_PRODUCT_TXT: Record<string, string> = {
    'NOI-X-TE': 'Servizio FACCIAMO TUTTO NOI PER TE',
    'XML': 'Servizio XML',
    'FATTAPP_ATTIVE': 'Servizio FattApp Attive',
    'FATTAPP_PASSIVE': 'Servizio FattApp Passive',
    'CONSERV2': 'Servizio CONSERVAZIONE DIGITALE FATTURE ELETTRONICHE (S0010, S0011)',
    'PASSIVE': 'Servizio FATTURE PASSIVE',
};

export const FATTURA_PRODUCT_ORDERS: Record<FatturaProductType, string[]> = {
    [FATTURA_PRODUCT.FP_MANUAL]: ['S0001', 'S0003', 'S0007'],
    [FATTURA_PRODUCT.FP_IMPORT]: ['S0002', 'S0004', 'S0008', 'S0009'], //count fai-da-te as XML
    [FATTURA_PRODUCT.FP_FATTAPP_ATTIVE]: ['S0017', 'S0018', 'S0020', 'S0022', 'S0024', 'S0025', 'S0026'],
    [FATTURA_PRODUCT.FP_FATTAPP_PASSIVE]: ['S0019', 'S0023'],
    [FATTURA_PRODUCT.FP_CONSERV2]: ['S0010', 'S0027'],
    [FATTURA_PRODUCT.FP_PASSIVE]: ['S0005'],
};

export const FATTURA_PRODUCT_ORDERS_REVERSE: Record<string, FatturaProductType> = Object.entries(FATTURA_PRODUCT_ORDERS)
    .reduce((obj, [k, v]) => { v.reduce((o, i) => { o[i] = k; return o; }, obj); return obj; }, {});

export const FATTURA_OPERATION = {
    FO_MANUAL: 'M',
    FO_MANUAL_FAI_DA_TE: 'F',
    FO_MANUAL_FATTAPP: 'A',
    FO_IMPORT_XML: 'X',
    FO_IMPORT_P7M: 'P',
    FO_IMPORT_CONSERV_OLD: 'C',
    FO_IMPORT_CONSERV2: 'D',
    //this is pseudo-operation for importing ( = C + passive), not stored in database
    FO_IMPORT_CONSERV_PASSIVE: 'CP',
    //this is pseudo-operation for importing ( = C + auto invoice), not stored in database
    FO_IMPORT_CONSERV_ACTIVE_AUTOINVOICE_CP_SIDE: 'CA',
    //this is virtual operation returned by stored procedure. database holds 'P' for passive
    FO_PASSIVE: 'V',
    FO_UNKNOWN: 'U',
    MapOperationToProduct: function(operation: string, permissions?: { fe_conservation_opt?: boolean }) {
        switch(operation) {
            case 'M':
                return FATTURA_PRODUCT.FP_MANUAL;
            //count fai-da-te as XML
            case 'F':
            case 'X':
            case 'P':
                return FATTURA_PRODUCT.FP_IMPORT;
            case 'A':
                return FATTURA_PRODUCT.FP_FATTAPP_ATTIVE;
            case 'C':
                if(!permissions || permissions.fe_conservation_opt)
                    return FATTURA_PRODUCT.FP_CONSERV2;
                return FATTURA_PRODUCT.FP_IMPORT;
            case 'D':
                return FATTURA_PRODUCT.FP_CONSERV2;
        }
        return null;
    },
} as const;

export const FATTURA_OPERATION_TXT: Record<string, string> = {
    'M': 'Manuale',
    'F': 'Fai-da-te',
    'A': 'FattApp Attive',
    'P': 'Importazione P7M',
    'X': 'Importazione XML',
    'C': 'Importazione P7M per conservazione',
    'D': 'Importazione P7M per conservazione',
};

export const INVOICE_INVALID_DIRECTION_FLAG = {
    VALID: null,
    INVALID: 1,
    SUSPICIOUS: -1,
} as const;

export const SRC_TXT: Record<string, string> = {
    'internal': 'Internal',
    'soap': 'SOAP',
    'passivefpa': 'Passive',
};

export const FATTURA_SRC = {
    //default
    internal: 'internal',
    soap: 'soap',
    passivefpa: 'passivefpa',
} as const;

export const TIPOLOGIA_CLIENTE = {
    IMPRESA: 'I', //Giuridic Person
    PROFESSIONISTA: 'P', //Physic Person
    PUBBLICA_AMMINISTRAZIONE: 'A', //Public Administration
} as const;

export const TIPOLOGIA_CLIENTE_TXT: Record<string, string> = {
    'I': 'IMPRESA',
    'P': 'PROFESSIONISTA',
    'A': 'ENTE P.A.',
};

export const TIPOLOGIA_CLIENTE_NAMES = {
    company_type_0: {
        name: 'altro',
        type: TIPOLOGIA_CLIENTE.IMPRESA,
        label: 'IMPRESA',
    },
    company_type_1: {
        name: 'azienda',
        type: TIPOLOGIA_CLIENTE.IMPRESA,
        label: 'Azienda',
    },
    company_type_2: {
        name: 'professionista',
        type: TIPOLOGIA_CLIENTE.PROFESSIONISTA,
        label: 'PROFESSIONISTA',
    },
    company_type_3: {
        name: 'commercialista',
        type: TIPOLOGIA_CLIENTE.IMPRESA,
        label: 'Commercialista',
    },
    company_type_4: {
        name: 'carburante',
        type: TIPOLOGIA_CLIENTE.IMPRESA,
        label: 'Carburante',
    },
    company_type_5: {
        name: 'centro_servizi',
        type: TIPOLOGIA_CLIENTE.IMPRESA,
        label: 'Centro Servizi',
    },
} as const;

export const CONSERVE_STATUS = {
    not_uploaded: 0,
    upload_error: 1,
    uploaded: 2,
    conserved: 3,
    //only for libri
    locked_for_conservation: 4,
} as const;

export const CONSERVE_STATUS_TXT: Record<string, string> = {
    '0': 'Non conservato',
    '1': 'Errore versamento',
    '2': 'Versamento completato',
    '3': 'Conservato',
    '4': 'Versamento richiesto',
};

export const TIPO_DESTINATARIO = {
    TIPO_FPA12: 'A',
    TIPO_FPR12: 'R',
    TIPO_PEC: 'P',
} as const;

export const TIPO_DESTINATARIO_TXT: Record<string, string> = {
    'A': 'PA',
    'R': 'B2B',
    'P': 'B2B PEC',
};

export const PRODUCT_LABELS = {
    'S0001': 'Completo "Facciamo Tutto Noi per Te"',
    'S0002': 'Completo XML',
    'S0003': 'Completo per Commercialisti',
    'S0004': 'Completo XML per Commercialisti',
    'S0005': 'FATTURE PASSIVE',
    'S0007': 'Completo per Centro Servizi',
    'S0008': 'Completo XML per Centro Servizi',
    'S0009': '"Fai Da Te"',

    'S0010': '',
    'S0011': '',
    'S0012': '',
    'S0013': '',
    'S0014': '',
    'S0015': '',

    'S0017': 'FattApp',
    'S0018': 'FattApp',
    'S0020': 'FattApp Staz. Servizi',
    'S0022': 'FattApp Commercialisti',
    'S0023': 'FattApp Solo Passive',
    'S0024': 'FattApp Cloud',
    'S0025': 'FattApp Centro Servizi',
} as const;

export const CENTURY_SOURCE_SMALL = {
    SRC_FATTURA: 'F',
    SRC_LIBRI: 'L',
    SRC_GENERALI: 'G',
    SRC_SYSTEM: 'S',
    SRC_SYSTEM_MDC: 'C',
} as const;

export const CENTURY_SUBJECT_PRODUCT_FLAG = {
    invoice: 1,
    libri: 2,
    pec: 4,
    generali: 8,
    system: 16,
} as const;

export const CENTURY_SUBJECT_PRODUCT_FLAG_TXT: Record<string, string> = {
    invoice: 'Fatture',
    libri: 'Libri',
    pec: 'PEC',
    generali: 'Custom',
    system: 'System',
};

export const CENTURY_SCHEMA_USAGE_FLAG = {
    'none': 0,
    'pdv': 1,
    'document': 2,
    'both': 3,
} as const;
export const CENTURY_SCHEMA_USAGE_FLAG_REVERSE: (keyof typeof CENTURY_SCHEMA_USAGE_FLAG)[] = [];
(function() {
    let id: keyof typeof CENTURY_SCHEMA_USAGE_FLAG;
    for(id in CENTURY_SCHEMA_USAGE_FLAG) {
        CENTURY_SCHEMA_USAGE_FLAG_REVERSE[CENTURY_SCHEMA_USAGE_FLAG[id]] = id;
    }
})();

export const VOLUME_STATUS = {
    VOLUME_STATUS_OPEN: '0',
    VOLUME_STATUS_CLOSED: '1',
    VOLUME_STATUS_READY_FOR_CONSERVATION: '2',
    VOLUME_STATUS_TIMESTAMP_PENDING: '3',
    VOLUME_STATUS_VERIFICATION_PENDING: '4',
    VOLUME_STATUS_CONSERVED: '5',
    VOLUME_STATUS_INVALID: '6',
    VOLUME_STATUS_DELETED: '7',
};

export const SINCRO_VERSION = {
    version1: 1,
    version2_LG2022compliant: 2,
} as const;

export const USE_VOLUME_STORAGE_TYPE = {
    UNPROTECTED: 0,
    PROTECTED: 1,
    DEFAULT: 2,
    FLAT: 3,
    SEVENZIP_FRAGMENTED: 4,
    SEVENZIP_FRAGMENTED_PROTECTED: 5,
    SEVENZIP: 6,
    SEVENZIP_PROTECTED: 7,
} as const;

export const VOLUME_STORAGE_TYPE = {
    LEGACY_AUTO: 0,
    FLAT: 1,
    SEVENZIP: 2,
    SEVENZIP_FRAGMENTED: 3,
} as const;

export const CENTURY_PROFILE_STATE = {
    ON_REVIEW: 1,
    REVIEWED: 2,
    ACTIVE: 3,
    ALWAYS_ACTIVE: 4,
} as const;

export const CENTURY_PROFILE_STATE_TXT: Record<string, string> = {
    [String(CENTURY_PROFILE_STATE.ON_REVIEW)]: 'In verifica',
    [String(CENTURY_PROFILE_STATE.REVIEWED)]: 'Pronto per MdC (verifcato)',
    [String(CENTURY_PROFILE_STATE.ACTIVE)]: 'Attivo',
    [String(CENTURY_PROFILE_STATE.ALWAYS_ACTIVE)]: 'Auto-attivato',
};

export const TEMPLATE_DOCUMENT_TYPES = {
    MDC: 'mdc',
    CONVENZIONE_DI_SERVIZIO: 'convenzione_di_servizio',
    GDPR: 'gdpr',
    ATTACHMENT: 'attachment',
    CUSTOM_ATTACHMENT: 'custom_attachment',
} as const;

export const MDC_WIZARD_MODE = {
    MDC: 1,
    CONVENZIONE_DI_SERVIZIO: 2,
    GDPR: 4,
    REJECT_INVALID: 8,
    REJECT_PARTIAL_DOCUMENTS: 16,
    REJECT_ALREADY_GENERATED: 32,
} as const;

export const INVOICE_CONSERV_SCHEMA = {
    doc_amministrativo: 'A',
    doc_informatico: 'I',
} as const;

export const ORDER_PAYMENT_METHOD = {
    PAYPAL: 'paypal',
    GESTPAY: 'gestpay',
    NONE: 'none',
    BANK_EXISTS: 'bank:exists',
    BANK: 'bank',
} as const;

export const EXPORT_MODE = {
    raw: 'raw',
    single_transaction: 'single_transaction',
    compatible: 'compatible',
} as const;

export const DISCOUNT_TYPE = {
    INTERNAL: 1,
    PARTNER: 2,
    AUTO_PARTNER: 4,
    NOFATTURA: 8,
    REFERRAL: 16,
    OLD_NO_REFERRAL: 1 | 2 | 4 | 8,
} as const;

export const _constants = {
    FATTURA_STATUS,
    FATTURA_VERSION,
    FATTURA_PRODUCT,
    FATTURA_PRODUCT_TXT,
    FATTURA_OPERATION,
    FATTURA_OPERATION_TXT,
    INVOICE_INVALID_DIRECTION_FLAG,
    SRC_TXT,
    FATTURA_SRC,
    TIPOLOGIA_CLIENTE,
    TIPOLOGIA_CLIENTE_TXT,
    TIPOLOGIA_CLIENTE_NAMES,
    CONSERVE_STATUS,
    CONSERVE_STATUS_TXT,
    TIPO_DESTINATARIO,
    TIPO_DESTINATARIO_TXT,
    PRODUCT_LABELS,
    CENTURY_SOURCE_SMALL,
    CENTURY_SUBJECT_PRODUCT_FLAG,
    CENTURY_SUBJECT_PRODUCT_FLAG_TXT,
    CENTURY_SCHEMA_USAGE_FLAG,
    CENTURY_SCHEMA_USAGE_FLAG_REVERSE,
    VOLUME_STATUS,
    SINCRO_VERSION,
    USE_VOLUME_STORAGE_TYPE,
    VOLUME_STORAGE_TYPE,
    CENTURY_PROFILE_STATE,
    CENTURY_PROFILE_STATE_TXT,
    TEMPLATE_DOCUMENT_TYPES,
    MDC_WIZARD_MODE,
    INVOICE_CONSERV_SCHEMA,
    ORDER_PAYMENT_METHOD,
    EXPORT_MODE,
    DISCOUNT_TYPE,
};
